/**
 * Text and headings set from the type scale in `textStyle`, so a screen picks a step by name and
 * gets its size, leading and weight together, instead of keeping its own copy of each.
 *
 * `Heading` separates what a heading is from how big it looks: `level` is its place in the outline
 * a screen reader walks, `size` its step on the scale. A card title inside a section is still an
 * `h3` when it is drawn at the section title's size.
 *
 * `tone` is the text colour by role. `danger`, `ok`, `warn` and `info` use the status text colours,
 * which meet 4.5:1 on every surface, and a status never rests on the colour alone: the words say it.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { textStyle } from "./textStyle.ts";
const tones = {
  primary: {
    kMwMTN: "xm8xz4y",
    $$css: true
  },
  secondary: {
    kMwMTN: "x1lm7cez",
    $$css: true
  },
  tertiary: {
    kMwMTN: "xyjz3t7",
    $$css: true
  },
  danger: {
    kMwMTN: "x6ygkrl",
    $$css: true
  },
  ok: {
    kMwMTN: "xdhxsyf",
    $$css: true
  },
  warn: {
    kMwMTN: "x1j243yh",
    $$css: true
  },
  info: {
    kMwMTN: "xzjdp0e",
    $$css: true
  }
};
const styles = {
  reset: {
    kogj98: "x1ghz6dp",
    kMv6JI: "x1cikjda",
    kN2L0X: "x1fzhlzt",
    $$css: true
  },
  heading: {
    kN2L0X: "x1w2vvpw",
    $$css: true
  },
  numeric: {
    kcqcaj: "x1l1vl4p",
    $$css: true
  },
  focusTarget: {
    k3XXqK: "x1t137rt",
    $$css: true
  }
};
export type TextVariant = "caption" | "label" | "body" | "metric";
export type TextTone = keyof typeof tones;
export interface TextProps {
  readonly children: ReactNode;
  readonly variant?: TextVariant;
  readonly tone?: TextTone;
  /** Tabular figures, for a number that lines up with others in a column. */
  readonly numeric?: boolean;
  /** `span` inside a line of other text, `p` for a paragraph. */
  readonly as?: "p" | "span";
  readonly id?: string;
  readonly testId?: string;
}
export function Text({
  children,
  variant = "body",
  tone = "primary",
  numeric = false,
  as: Tag = "p",
  id,
  testId
}: TextProps) {
  return <Tag id={id} data-testid={testId} {...stylex.props(styles.reset, textStyle[variant], tones[tone], numeric && styles.numeric)}>
      {children}
    </Tag>;
}
export interface HeadingProps {
  readonly children: ReactNode;
  readonly level: 1 | 2 | 3 | 4;
  /** Defaults to the page title for level 1 and the section title below it. */
  readonly size?: "pageTitle" | "pageTitleCompact" | "sectionTitle" | "label";
  readonly tone?: TextTone;
  readonly id?: string;
  /** Takes focus when it appears, for a screen that swaps whole states in place. */
  readonly focusOnMount?: boolean;
  readonly testId?: string;
}
const TAGS = {
  1: "h1",
  2: "h2",
  3: "h3",
  4: "h4"
} as const;
export function Heading({
  children,
  level,
  size,
  tone = "primary",
  id,
  focusOnMount = false,
  testId
}: HeadingProps) {
  const Tag = TAGS[level];
  const step = size ?? (level === 1 ? "pageTitle" : "sectionTitle");
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (focusOnMount) ref.current?.focus();
  }, [focusOnMount]);
  return <Tag ref={ref} id={id} tabIndex={focusOnMount ? -1 : undefined} data-testid={testId} {...stylex.props(styles.reset, styles.heading, textStyle[step], tones[tone], focusOnMount && styles.focusTarget)}>
      {children}
    </Tag>;
}